import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { Block, Inline } from './markdown';

export function sectionAnchorId(prefix: string, index: number): string {
  return `${prefix}-s${index}`;
}

interface LinkProps {
  href: string;
  children: React.ReactNode;
}

// In-app links ("/assets") navigate through react-router (which opens/focuses
// an internal tab); "/user-guide?chapter=<id>" (cross-chapter) too; "/guide/..." (static assets) and external links open
// in a new browser tab.
function GuideLink({ href, children }: LinkProps) {
  const navigate = useNavigate();
  const inApp = href.startsWith('/') && !href.startsWith('/guide/');
  if (inApp) {
    return (
      <a
        href={href}
        onClick={(e) => {
          if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          navigate(href);
        }}
      >
        {children}
      </a>
    );
  }
  if (href.startsWith('#')) return <a href={href}>{children}</a>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

function renderInline(nodes: Inline[]): React.ReactNode[] {
  return nodes.map((n, i) => {
    switch (n.t) {
      case 'text':
        return <React.Fragment key={i}>{n.text}</React.Fragment>;
      case 'bold':
        return <strong key={i}>{renderInline(n.children)}</strong>;
      case 'italic':
        return <em key={i}>{renderInline(n.children)}</em>;
      case 'code':
        return <code key={i}>{n.text}</code>;
      case 'link':
        return (
          <GuideLink key={i} href={n.href}>
            {renderInline(n.children)}
          </GuideLink>
        );
      case 'image': {
        const safe = /^(\/|https?:\/\/)/i.test(n.src) ? n.src : '';
        return safe ? <img key={i} className="user-guide__img" src={safe} alt={n.alt} loading="lazy" /> : null;
      }
      default:
        return null;
    }
  });
}

interface RenderCtx {
  idPrefix: string;
  /** Skip the first `#` heading (the chapter header already shows the title). */
  skipFirstH1: boolean;
}

function renderBlocks(blocks: Block[], ctx: RenderCtx, state: { h2: number; h1Skipped: boolean }, tight = false): React.ReactNode[] {
  return blocks.map((b, i) => {
    switch (b.t) {
      case 'heading': {
        if (b.level === 1) {
          if (ctx.skipFirstH1 && !state.h1Skipped) {
            state.h1Skipped = true;
            return null;
          }
          return <h2 key={i} className="user-guide__h1-extra">{renderInline(b.children)}</h2>;
        }
        if (b.level === 2) {
          const id = sectionAnchorId(ctx.idPrefix, state.h2++);
          return (
            <h2 key={i} id={id} className="user-guide__h2">
              {renderInline(b.children)}
            </h2>
          );
        }
        return (
          <h3 key={i} className="user-guide__h3">
            {renderInline(b.children)}
          </h3>
        );
      }
      case 'paragraph':
        return tight ? (
          <React.Fragment key={i}>{renderInline(b.children)}</React.Fragment>
        ) : (
          <p key={i}>{renderInline(b.children)}</p>
        );
      case 'list': {
        const Tag = b.ordered ? 'ol' : 'ul';
        return (
          <Tag key={i}>
            {b.items.map((item, j) => (
              <li key={j}>{renderBlocks(item, ctx, state, item.length === 1 && item[0].t === 'paragraph')}</li>
            ))}
          </Tag>
        );
      }
      case 'table':
        return (
          <div key={i} className="user-guide__table-wrap">
            <table>
              <thead>
                <tr>
                  {b.head.map((c, j) => (
                    <th key={j}>{renderInline(c)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {b.rows.map((r, j) => (
                  <tr key={j}>
                    {r.map((c, k) => (
                      <td key={k}>{renderInline(c)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case 'callout':
        return (
          <aside key={i} className={`user-guide__callout user-guide__callout--${b.kind}`}>
            {renderBlocks(b.children, ctx, state)}
          </aside>
        );
      case 'quote':
        return <blockquote key={i}>{renderBlocks(b.children, ctx, state)}</blockquote>;
      case 'code':
        return (
          <pre key={i} dir="ltr">
            <code>{b.text}</code>
          </pre>
        );
      case 'hr':
        return <hr key={i} />;
      default:
        return null;
    }
  });
}

export function MarkdownView({
  blocks,
  idPrefix,
  skipFirstH1 = true,
}: {
  blocks: Block[];
  idPrefix: string;
  skipFirstH1?: boolean;
}) {
  return <>{renderBlocks(blocks, { idPrefix, skipFirstH1 }, { h2: 0, h1Skipped: false })}</>;
}
